import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import {
  changePassword,
  disable2FA,
  logoutEverywhere,
  me,
  regenerateRecoveryCodes,
} from "../api/auth";

export function SecurityPage() {
  const { api, setSession, state } = useAuth();
  const [twofaEnabled, setTwofaEnabled] = useState<boolean | null>(null);

  // password change
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwOk, setPwOk] = useState<string | null>(null);

  // recovery-code regen
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null);
  const [regenBusy, setRegenBusy] = useState(false);

  // 2FA disable
  const [disableCode, setDisableCode] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);

  // logout-everywhere
  const [loBusy, setLoBusy] = useState(false);
  const [loMsg, setLoMsg] = useState<string | null>(null);

  useEffect(() => {
    me(api).then((r) => setTwofaEnabled(r.account.two_factor_enabled)).catch(() => setTwofaEnabled(null));
  }, [api]);

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    setPwBusy(true); setPwError(null); setPwOk(null);
    try {
      const r = await changePassword(api, current, next);
      if (state.account) {
        setSession({ access_token: r.access_token, refresh_token: r.refresh_token, account: state.account });
      }
      setPwOk("Password changed. Sessions on other devices were revoked.");
      setCurrent(""); setNext("");
    } catch (e) {
      setPwError(e instanceof ApiError ? e.message : "change failed");
    } finally { setPwBusy(false); }
  }

  async function onRegenerate() {
    if (!confirm("Issue new recovery codes? Old codes will immediately stop working.")) return;
    setRegenBusy(true); setNewRecoveryCodes(null);
    try {
      const r = await regenerateRecoveryCodes(api);
      setNewRecoveryCodes(r.recovery_codes);
    } catch { /* displayed via page re-render */ }
    finally { setRegenBusy(false); }
  }

  async function onDisable(e: FormEvent) {
    e.preventDefault();
    setDisableBusy(true); setDisableError(null);
    try {
      await disable2FA(api, disableCode);
      setTwofaEnabled(false);
      setDisableCode("");
    } catch (e) {
      setDisableError(e instanceof ApiError ? e.message : "disable failed");
    } finally { setDisableBusy(false); }
  }

  async function onLogoutEverywhere() {
    if (!confirm("Revoke refresh tokens on every device except this one?")) return;
    setLoBusy(true); setLoMsg(null);
    try {
      const r = await logoutEverywhere(api);
      setLoMsg(`Revoked ${r.revoked} refresh tokens.`);
    } catch (e) {
      setLoMsg(e instanceof ApiError ? e.message : "failed");
    } finally { setLoBusy(false); }
  }

  return (
    <div className="vstack">
      <div>
        <h1>Security</h1>
        <p className="meta">Password, two-factor, and sign-in lifecycle.</p>
      </div>

      <form className="card" onSubmit={onChangePassword}>
        <h2>Change password</h2>
        <p className="meta">All other sessions will be revoked. Your current session stays.</p>
        <div className="field">
          <label htmlFor="current">Current password</label>
          <input id="current" type="password" value={current} required onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="next">New password (≥ 12 chars)</label>
          <input id="next" type="password" value={next} required onChange={(e) => setNext(e.target.value)} />
        </div>
        {pwError && <div className="error">{pwError}</div>}
        {pwOk && <div className="ok">{pwOk}</div>}
        <button className="btn primary" type="submit" disabled={pwBusy || next.length < 12}>
          {pwBusy ? "Changing…" : "Change password"}
        </button>
      </form>

      <div className="card">
        <h2>Two-factor authentication</h2>
        {twofaEnabled === null && <p className="meta">Loading…</p>}
        {twofaEnabled === false && (
          <div className="hstack">
            <p className="meta">2FA is not enabled.</p>
            <Link className="btn primary" to="/2fa/enroll">Enable 2FA</Link>
          </div>
        )}
        {twofaEnabled === true && (
          <>
            <p className="ok">2FA is enabled.</p>
            <button className="btn" onClick={onRegenerate} disabled={regenBusy}>
              {regenBusy ? "Generating…" : "Regenerate recovery codes"}
            </button>
            {newRecoveryCodes && (
              <div style={{ marginTop: 16 }}>
                <p className="meta">New recovery codes. Store somewhere safe — old codes no longer work.</p>
                <div className="recovery-list">
                  {newRecoveryCodes.map((c) => <code key={c}>{c}</code>)}
                </div>
              </div>
            )}
            <form onSubmit={onDisable} style={{ marginTop: 24 }}>
              <h2>Disable 2FA</h2>
              <p className="meta">Enter your current 6-digit code to confirm.</p>
              <div className="field">
                <input type="text" inputMode="numeric" placeholder="Current code"
                  value={disableCode} required onChange={(e) => setDisableCode(e.target.value)} />
              </div>
              {disableError && <div className="error">{disableError}</div>}
              <button className="btn danger" type="submit" disabled={disableBusy || disableCode.length < 6}>
                {disableBusy ? "Disabling…" : "Disable 2FA"}
              </button>
            </form>
          </>
        )}
      </div>

      <div className="card">
        <h2>Sign out everywhere</h2>
        <p className="meta">Forces every session except this one to refresh-fail on their next attempt.</p>
        <button className="btn danger" onClick={onLogoutEverywhere} disabled={loBusy}>
          {loBusy ? "Revoking…" : "Sign out everywhere"}
        </button>
        {loMsg && <div className="meta" style={{ marginTop: 12 }}>{loMsg}</div>}
      </div>
    </div>
  );
}
